/**
 * Standard Integration Dialog Layout
 * 
 * Provides consistent structure across all integration dialog views:
 * - Header: Title, optional back button (left), optional action button (right)
 * - Content: Scrollable main content area
 * - Footer: Optional bottom actions (empty if not needed)
 */

import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from '@/components/icons'

interface IntegrationDialogLayoutProps {
  // Header
  title: string | ReactNode
  onBack?: () => void
  headerAction?: ReactNode
  
  // Content (scrollable)
  children: ReactNode
  
  // Footer (optional)
  footer?: ReactNode
}

export function IntegrationDialogLayout({
  title,
  onBack,
  headerAction,
  children,
  footer
}: IntegrationDialogLayoutProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Header - Fixed at top */}
      <div className="flex-shrink-0">
        <div className="flex items-center gap-4 px-2 pt-6 pb-4">
          {/* Left: Back button */}
          <div className="flex-shrink-0">
            {onBack && (
              <Button 
                variant="secondary" 
                onClick={onBack}
                className="flex items-center gap-2 md:px-4 px-2 md:rounded-md rounded-full"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden md:inline">Back</span>
              </Button>
            )}
          </div>
          
          {/* Center: Title */}
          <h2 className="text-lg font-semibold truncate text-center flex-1">
            {title}
          </h2>
          
          {/* Right: Action button */}
          <div className="flex-shrink-0 min-w-8 flex justify-end">
            {headerAction}
          </div>
        </div>
      </div>

      {/* Content - Scrollable wrapper, takes remaining space */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Content container with padding - expands with content */}
        <div className="px-2 py-4">
          {children}
        </div>
      </div>

      {/* Footer - Fixed at bottom (only rendered if provided) */}
      {footer && (
        <div className="flex-shrink-0 border-t">
          <div className="px-2 py-4">
            {footer}
          </div>
        </div>
      )}
    </div>
  )
}

